import React, { useState, useCallback } from "react";
import Button from "../generic_components/Button";

const PromptButton = ({ onModalToggle }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const categoryRows = [
    // First row
    [
      { icon: "🎬", text: "Reel & Short" },
      { icon: "📣", text: "Advertisement" },
      { icon: "🎮", text: "Gaming" },
      { icon: "🎬", text: "Film" },
      { icon: "🎙️", text: "Podcast" }
    ],
    // Second row
    [
      { icon: "📹", text: "Vlog" },
      { icon: "💡", text: "Presentation" },
      { icon: "❤️", text: "Wellness" },
      { icon: "🛍️", text: "Retail" },
      { icon: "📅", text: "Event" }
    ]
  ];

  const handleCategoryClick = (category, categoryIcon) => {
    console.log("Category clicked:", category);
    
    // Reset file state when opening modal
    setSelectedFile(null);
    setIsDragging(false);
    
    // Open a modal with proper file handling
    onModalToggle({
      name: "div",
      props: {
        className: "bg-[#1D1B1D] text-white rounded-xl px-6 py-5 w-full max-w-md mx-auto flex flex-col"
      },
      children: [
        // Header with title and close button
        {
          name: "div",
          props: {
            className: "flex justify-between items-center mb-5"
          },
          children: [
            {
              name: "h2",
              props: {
                className: "text-xl font-medium",
                text: `Create ${category} Music`
              }
            },
            {
              name: "div",
              props: {
                className: "cursor-pointer p-1 hover:bg-gray-700 rounded-full",
                trigger: "closeModal"
              },
              children: [
                {
                  name: "svg",
                  props: {
                    className: "w-5 h-5",
                    viewBox: "0 0 24 24",
                    fill: "none",
                    stroke: "currentColor",
                    strokeWidth: "2",
                    strokeLinecap: "round",
                    strokeLinejoin: "round"
                  },
                  children: [
                    {
                      name: "path",
                      props: {
                        d: "M18 6L6 18"
                      }
                    },
                    {
                      name: "path",
                      props: {
                        d: "M6 6L18 18"
                      }
                    }
                  ]
                }
              ]
            }
          ]
        },
        // Description text
        {
          name: "p",
          props: {
            className: "text-gray-300 mb-5 text-center",
            text: "Create AI music for your content. Select a file to upload or describe your needs."
          }
        },
        // File upload section - implemented as a fully self-contained component
        {
          name: "FileUploader",
          props: {
            onFileSelected: (file) => {
              setSelectedFile(file);
              console.log("File selected:", file);
            },
            currentFile: selectedFile,
            resetFile: () => setSelectedFile(null)
          }
        },
        // Generate button
        {
          name: "button",
          props: {
            className: "bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-2 px-4 rounded-lg hover:opacity-90 transition-opacity w-full",
            text: "Generate Music",
            onClick: () => {
              console.log("Generating music with file:", selectedFile);
              // Add your generation logic here
            }
          }
        }
      ]
    });
  };

  return (
    <div className="flex flex-col items-center gap-4 w-full text-sm" id="suggestions-container">
      {categoryRows.map((row, rowIndex) => (
        <div 
          className={`flex justify-center items-center space-x-2 ${rowIndex === 0 ? 'mb-4' : ''}`}
          id={`row-${rowIndex+1}-categories`}
          key={`category-row-${rowIndex}`}
        >
          {row.map((category, index) => (
            <React.Fragment key={`category-${rowIndex}-${index}`}>
              <Button 
                className="h-7 px-2 flex items-center gap-1 hover:bg-[rgba(255,255,255,0.05)] rounded-full text-xs font-medium text-white cursor-pointer"
                caption={category.text}
                musicEmoji={category.icon}
                onClick={() => handleCategoryClick(category.text, category.icon)}
              />
              {index < row.length - 1 && (
                <div className="w-px h-5 bg-white opacity-20" />
              )}
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
};

// Create a self-contained FileUploader component
const FileUploader = ({ onFileSelected, currentFile, resetFile }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onFileSelected(file);
    }
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      onFileSelected(file);
    }
  }, [onFileSelected]);

  const handleClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  return (
    <div 
      className={`bg-[#2E2E2D] rounded-lg p-6 mb-5 border ${isDragging ? 'border-blue-500 bg-[#3a3a39]' : 'border-[#3E3E3D] border-dashed'}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".mp4,.mov,.mp3,.wav"
        onChange={handleFileChange}
      />
      <div className="flex flex-col items-center justify-center text-center">
        {currentFile ? (
          // Display selected file info
          <div className="flex flex-col items-center">
            <svg 
              className="w-12 h-12 mb-3 text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mb-1 text-sm font-medium">{currentFile.name}</p>
            <p className="text-xs text-gray-400">
              {(currentFile.size / (1024 * 1024)).toFixed(2)} MB
            </p>
            <button
              className="mt-3 text-xs text-blue-400 hover:text-blue-300"
              onClick={(e) => {
                e.stopPropagation();
                resetFile();
              }}
            >
              Remove file
            </button>
          </div>
        ) : (
          // Default upload UI
          <>
            <svg
              className="w-12 h-12 mb-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
            <p className="mb-2 text-sm font-medium">
              {isDragging ? "Drop file here" : "Click to upload or drag and drop"}
            </p>
            <p className="text-xs text-gray-400">
              MP4, MOV, MP3, WAV up to 500MB
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PromptButton;